// src/features/tag/TagBatchUtils.ts

import { TAG_CONSTRAINTS } from '@/lib/constants/tag';

export type BatchTagMode = 'add' | 'remove';

/** Minimal media shape the batch tag planner needs — works for videos and streams. */
export interface BatchTagItem {
  id: number;
  title: string;
  tags: string[];
}

export interface BatchTagPlanEntry {
  id: number;
  nextTags: string[];
}

export interface BatchTagPlan {
  /** Items whose tag list actually changes, with their full new tag arrays. */
  changed: BatchTagPlanEntry[];
  /** Items already in the desired state — no request needed. */
  unchangedCount: number;
  /** Add mode only: items skipped because the result would exceed the per-media tag limit. */
  overLimit: BatchTagItem[];
}

/**
 * Pure planning logic for batch add/remove tags.
 * Tag matching is case-insensitive (consistent with TagInput dedup); existing
 * tag casing on an item is never rewritten — only additions/removals change
 * the list, so applying a plan is idempotent and retry-safe.
 */
export class TagBatchUtils {
  static planBatch(items: BatchTagItem[], mode: BatchTagMode, tags: string[]): BatchTagPlan {
    const wanted = TagBatchUtils.normalizeInput(tags);
    const changed: BatchTagPlanEntry[] = [];
    const overLimit: BatchTagItem[] = [];
    let unchangedCount = 0;

    for (const item of items) {
      if (wanted.length === 0) {
        unchangedCount++;
        continue;
      }

      const currentLower = new Set(item.tags.map((t) => t.toLowerCase()));

      if (mode === 'add') {
        const additions = wanted.filter((t) => !currentLower.has(t.toLowerCase()));
        if (additions.length === 0) {
          unchangedCount++;
          continue;
        }
        const nextTags = [...item.tags, ...additions];
        if (nextTags.length > TAG_CONSTRAINTS.MAX_TAGS_PER_MEDIA) {
          overLimit.push(item);
          continue;
        }
        changed.push({ id: item.id, nextTags });
      } else {
        const removeSet = new Set(wanted.map((t) => t.toLowerCase()));
        const nextTags = item.tags.filter((t) => !removeSet.has(t.toLowerCase()));
        if (nextTags.length === item.tags.length) {
          unchangedCount++;
          continue;
        }
        changed.push({ id: item.id, nextTags });
      }
    }

    return { changed, unchangedCount, overLimit };
  }

  /**
   * Per-tag impact: how many items each tag actually changes
   * (add: items missing it; remove: items that have it).
   */
  static impactCounts(items: BatchTagItem[], mode: BatchTagMode, tags: string[]): Map<string, number> {
    const wanted = TagBatchUtils.normalizeInput(tags);
    const counts = new Map<string, number>();
    for (const tag of wanted) {
      const tagLower = tag.toLowerCase();
      let n = 0;
      for (const item of items) {
        const has = item.tags.some((t) => t.toLowerCase() === tagLower);
        if (mode === 'add' ? !has : has) n++;
      }
      counts.set(tag, n);
    }
    return counts;
  }

  /**
   * Union of tags present on the given items — remove-mode suggestion source.
   * First-seen casing wins; sorted case-insensitively.
   */
  static tagsOnItems(items: BatchTagItem[]): string[] {
    const byLower = new Map<string, string>();
    for (const item of items) {
      for (const tag of item.tags) {
        const lower = tag.toLowerCase();
        if (!byLower.has(lower)) byLower.set(lower, tag);
      }
    }
    return [...byLower.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  /** Trim, drop empties, dedupe case-insensitively (first casing wins). */
  private static normalizeInput(tags: string[]): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const raw of tags) {
      const tag = raw.trim();
      if (!tag) continue;
      const lower = tag.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);
      result.push(tag);
    }
    return result;
  }
}
